import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '~/components/ui/button';
import { downloadAppHtml } from '~/features/apps/app-download';
import { downloadBlob, saveBlob } from '~/features/home/briefing-utils';
import { downloadButtonState, downloadTargetOf } from './canvas-download';
import type { CanvasItem } from './collect-items';

/**
 * FRONT-2669 — the Canvas header's Download button. Documents download their SAVED
 * body (never an unsaved Edit draft) as Markdown; a Markdown file uses its raw
 * route; a sketch or deck downloads its HTML through the Apps download route.
 */
export function CanvasDownloadButton({
  item,
  savedContent,
  draftUnsaved,
}: {
  item: CanvasItem;
  /** A document's saved body from the same detail query the viewer reads; null until it has loaded. */
  savedContent: string | null;
  draftUnsaved: boolean;
}) {
  const state = downloadButtonState({ kind: item.kind, documentLoaded: savedContent !== null, draftUnsaved });
  const onClick = () => {
    const target = downloadTargetOf(item);
    if (target.kind === 'document') {
      if (savedContent !== null) downloadBlob(savedContent, target.filename, 'text/markdown');
      return;
    }
    if (target.kind === 'file') {
      // Same-origin route that answers `Content-Disposition: attachment`; fetched so a failure toasts instead of saving an error page.
      void fetch(target.url, { credentials: 'include' })
        .then(async (res) => {
          if (!res.ok) throw new Error(`Couldn't download this file (${res.status}).`);
          saveBlob(await res.blob(), target.filename);
        })
        .catch((e: Error) => toast.error(e.message));
      return;
    }
    void downloadAppHtml(target.path, target.title, target.fallbackWord).catch((e: Error) => toast.error(e.message));
  };
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      tooltip={state.tooltip}
      aria-label="Download"
      data-testid="canvas-download"
      disabled={state.disabled}
      onClick={onClick}
    >
      <Download size={16} />
    </Button>
  );
}
